import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { tokens } from '../../../design';

/**
 * Appeal detail-drawer footer (AUT-130). Two conditional buttons + audit
 * note. Both actions are role + permission + audit gated server-side —
 * this is presentation only.
 *
 *   - "Начать рассмотрение" (review_start) — rendered ONLY when
 *     `canReviewStart` (status === `pending`). Ghost button per the mockup.
 *   - "Вынести решение" (resolve) — rendered when `canResolve` (status !==
 *     `resolved`). Primary (filled) button.
 *
 * When the appeal is `resolved` neither button renders — the drawer body
 * surfaces the persisted decision read-only.
 */
export interface AppealDetailFooterProps {
  canReviewStart: boolean;
  canResolve: boolean;
  busy: boolean;
  onReviewStart: () => void;
  onResolve: () => void;
}

export const AppealDetailFooter = ({
  canReviewStart,
  canResolve,
  busy,
  onReviewStart,
  onResolve,
}: AppealDetailFooterProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: '14px 24px',
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {canReviewStart ? (
        <Button
          variant="outlined"
          color="inherit"
          disabled={busy}
          onClick={onReviewStart}
          startIcon={<AddIcon />}
        >
          {t('appeals.actions.reviewStart')}
        </Button>
      ) : null}
      {canResolve ? (
        <Button
          variant="contained"
          color="primary"
          disabled={busy}
          onClick={onResolve}
          startIcon={<CheckIcon />}
        >
          {t('appeals.actions.resolve')}
        </Button>
      ) : null}
      <Box
        sx={{
          ml: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          fontSize: 11,
          color: tokens.text.tertiary,
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 12 }} />
        {t('appeals.actions.auditNote')}
      </Box>
    </Box>
  );
};
